import React from 'react';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import Header from '../components/Header';

export default function AboutPage() {
  const phoneNumber = "(630) 715-8829";
  const companyName = "Partners Plumbing Co.";
  const address = "Kane County, IL 60177";
  const email = "info@partnersinplumbing.com";

  return (
    <>
      <Head>
        <title>{`About ${companyName} | Licensed Plumber Geneva, IL & Kane County`}</title>
        <meta name="description" content={`About ${companyName} - Licensed plumber in Geneva, St Charles & Kane County, IL. Reliable plumber near me. Best plumber Kane County. Trusted local plumbing contractor.`} />
        <meta name="keywords" content="best plumber Kane County, reliable plumber Kane County, licensed plumber Kane County, plumber reviews Kane County, who is the best plumber in Kane County, affordable plumber Kane County" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://partnersinplumbing.com/about" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <div className="min-h-screen bg-white font-inter">
        <Header currentPage="/about" phoneNumber={phoneNumber} companyName={companyName} />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-900 text-white py-12 md:py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 lg:mb-6 leading-tight">About {companyName} - Best Plumber in Geneva, IL & Kane County</h1>
                <p className="text-lg sm:text-xl mb-6 lg:mb-8 text-slate-100 leading-relaxed">
                  The most reliable plumber near me in Kane County - Licensed, insured plumber serving Geneva, St Charles, and South Elgin with affordable plumbing services. Trusted local plumbing contractor.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <a href={`tel:${phoneNumber}`} className="bg-yellow-500 text-slate-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-yellow-400 transition-all duration-200 shadow-lg hover:shadow-xl text-center">
                    Call {phoneNumber}
                  </a>
                  <Link href="/contact" className="bg-transparent border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-white hover:text-slate-900 transition-all duration-200 text-center">
                    Get Free Estimate
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="bg-white/10 p-8 rounded-xl backdrop-blur-sm border border-white/20 shadow-2xl">
                  <h3 className="text-2xl font-bold mb-4">Licensed & Experienced Excellence</h3>
                  <p className="text-slate-100 mb-4 leading-relaxed">Trusted by thousands of customers throughout Kane County for reliable, professional plumbing services</p>
                  <div className="space-y-3">
                    <div className="flex items-center text-yellow-400">
                      <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                        <div className="w-4 h-4 bg-yellow-600 rounded-full"></div>
                      </div>
                      <span className="font-semibold">Licensed & Insured</span>
                    </div>
                    <div className="flex items-center text-yellow-400">
                      <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                        <div className="w-4 h-4 bg-yellow-600 rounded-full"></div>
                      </div>
                      <span className="font-semibold">Quality Workmanship</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Trusted Plumbing Professionals</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We've built our reputation on quality workmanship, honest pricing, and exceptional customer service throughout Kane County and surrounding areas.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-10 h-10 bg-white rounded-full"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Licensed</h3>
                <p className="text-gray-600">Illinois State Licensed Plumber</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-10 h-10 bg-white rounded-full"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Insured</h3>
                <p className="text-gray-600">Fully Insured & Bonded</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-10 h-10 bg-white rounded-full"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Local</h3>
                <p className="text-gray-600">Kane County Experts</p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    {companyName} was founded with a simple but powerful mission: 
                    to provide honest, reliable plumbing services to families and businesses throughout Kane County. 
                    What started as a small, local operation has grown into one of the most trusted plumbing companies 
                    in the region.
                  </p>
                  <p>
                    Our founder, recognizing the need for dependable plumbing services in Kane County, 
                    started the company with a commitment to quality workmanship and exceptional customer service. 
                    Over the years, we've built our reputation one satisfied customer at a time, earning countless positive reviews from homeowners and business owners alike.
                  </p>
                  <p>
                    Today, we're proud to be Kane County's most trusted plumbing company, with a team of skilled 
                    professionals who are passionate about solving your plumbing problems quickly and effectively. 
                    We've handled everything from simple faucet repairs to complex commercial installations, 
                    and we're committed to bringing that same level of expertise to every job we undertake.
                  </p>
                  <p>
                    As we continue to grow, our core values remain unchanged: integrity, quality, and customer satisfaction. 
                    These principles guide everything we do, from the way we answer your calls to the way we complete your projects.
                  </p>
                </div>
              </div>
              <div className="space-y-6">
              <div className="bg-slate-50 p-8 rounded-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Company Credentials</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                        <div className="w-6 h-6 bg-white rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Licensed Plumbing Contractor</h4>
                        <p className="text-gray-600">Illinois State licensed plumbing contractor</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mr-4">
                        <div className="w-6 h-6 bg-white rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Fully Insured</h4>
                        <p className="text-gray-600">General Liability & Workers' Compensation</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4">
                        <div className="w-6 h-6 bg-white rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">EPA Certified</h4>
                        <p className="text-gray-600">Lead-Safe Renovation & Backflow Prevention</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4">
                        <div className="w-6 h-6 bg-white rounded-full"></div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Local Expertise</h4>
                        <p className="text-gray-600">Trusted Kane County plumbing contractor</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>



        {/* Our Values */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
              <p className="text-xl text-gray-600">The principles that guide everything we do</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Integrity & Honesty</h3>
                <p className="text-gray-600 mb-4">We believe in doing the right thing, even when no one is watching. 
                Our technicians provide honest assessments and will never recommend unnecessary work. 
                We explain all options clearly and let you make informed decisions about your plumbing needs.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Quality Workmanship</h3>
                <p className="text-gray-600 mb-4">Every job is completed to the highest standards using quality materials 
                and proven techniques. We take pride in our work and stand behind it with comprehensive warranties. 
                Our goal is to fix it right the first time, every time.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                    <div className="w-6 h-6 bg-white rounded-full"></div>
                  </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Customer-First Service</h3>
                <p className="text-gray-600 mb-4">Your satisfaction is our top priority. We arrive on time, work efficiently, 
                and clean up after ourselves. We're not satisfied until you're completely happy with our work. 
                Our commitment to quality ensures you receive exceptional service every time.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                  <div className="w-6 h-6 bg-white rounded-full"></div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Community Commitment</h3>
                <p className="text-gray-600 mb-4">As a local Kane County business, we're invested in our community. 
                We sponsor local events, support local charities, and employ local residents. 
                When you choose us, you're supporting a business that gives back to the community we all call home.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Certifications & Licenses */}
        <section className="py-16 bg-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Licensed, Certified & Insured</h2>
              <p className="text-xl text-slate-100">Fully licensed, bonded, and insured for your complete protection</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                             <div className="text-center">
                 <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                   <div className="w-8 h-8 bg-white rounded-full"></div>
                 </div>
                 <h3 className="text-xl font-semibold mb-2">Licensed Plumbing Contractor</h3>
                 <p className="text-slate-100">State licensed plumbers with ongoing education requirements</p>
                 <p className="text-slate-300 text-sm mt-2">Illinois licensed plumbing contractor</p>
               </div>
                             <div className="text-center">
                 <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                   <div className="w-8 h-8 bg-white rounded-full"></div>
                 </div>
                 <h3 className="text-xl font-semibold mb-2">Fully Insured & Bonded</h3>
                 <p className="text-slate-100">General liability and workers' compensation insurance</p>
                 <p className="text-slate-300 text-sm mt-2">Coverage includes property damage protection</p>
               </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full"></div>
                </div>
                <h3 className="text-xl font-semibold mb-2">EPA Certified</h3>
                <p className="text-slate-100">Lead-Safe Renovation and Backflow Prevention certified</p>
                <p className="text-slate-300 text-sm mt-2">Environmental protection compliance</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full"></div>
                </div>
                <h3 className="text-xl font-semibold mb-2">Trusted Local Contractor</h3>
                <p className="text-slate-100">Serving Kane County with integrity</p>
                <p className="text-slate-300 text-sm mt-2">Commitment to ethical business practices</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full"></div>
                </div>
                <h3 className="text-xl font-semibold mb-2">Background Checked</h3>
                <p className="text-slate-100">All technicians undergo comprehensive background checks</p>
                <p className="text-slate-300 text-sm mt-2">Your safety and security are our priority</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full"></div>
                </div>
                <h3 className="text-xl font-semibold mb-2">Continuing Education</h3>
                <p className="text-slate-100">Regular training on latest techniques and technologies</p>
                <p className="text-slate-300 text-sm mt-2">Staying current with industry best practices</p>
              </div>
            </div>
          </div>
        </section>

        {/* Community Involvement */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Community Involvement</h2>
              <p className="text-xl text-gray-600">Giving back to the Kane County community we serve</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full"></div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Local Charity Support</h3>
                <p className="text-gray-600">We regularly donate services to local charities and families in need, 
                including emergency plumbing repairs for seniors and low-income families.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full"></div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Youth Sports Sponsorship</h3>
                <p className="text-gray-600">Proud sponsors of local youth sports teams in Aurora, Elgin, and surrounding communities, 
                supporting the next generation of Kane County residents.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 bg-white rounded-full"></div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Local Employment</h3>
                <p className="text-gray-600">We hire locally and provide good-paying jobs with benefits to Kane County residents, 
                contributing to the local economy and community growth.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Experience the Difference?</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied customers who trust {companyName} for all their plumbing needs. 
              Experience the difference that professional service makes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${phoneNumber}`} className="bg-slate-800 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-slate-900 transition-colors">
                Call {phoneNumber}
              </a>
              <Link href="/contact" className="bg-transparent border-2 border-slate-800 text-slate-800 px-8 py-4 rounded-lg font-bold text-lg hover:bg-slate-800 hover:text-white transition-colors">
                Get Free Estimate
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">{companyName}</h3>
                <p className="text-gray-300 mb-4">Professional plumbing services you can trust. Licensed, insured, and reliable serving Kane County.</p>
                <a href={`tel:${phoneNumber}`} className="text-yellow-400 font-bold text-lg hover:text-yellow-300 block mb-2">
                  {phoneNumber}
                </a>
                <a href={`mailto:${email}`} className="text-gray-300 hover:text-white block mb-2">
                  {email}
                </a>
                <p className="text-gray-300 text-sm">
                  {address}
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/services/broken-pipe" className="hover:text-white">Broken Pipe Repair</Link></li>
                  <li><Link href="/services/leaky-plumbing" className="hover:text-white">Leak Repair</Link></li>
                  <li><Link href="/services/water-heater-replacement" className="hover:text-white">Water Heater Replacement</Link></li>
                  <li><Link href="/services/reverse-osmosis" className="hover:text-white">Reverse Osmosis</Link></li>
                  <li><Link href="/services/toilet-installation" className="hover:text-white">Toilet Installation</Link></li>
                  <li><Link href="/services/emergency-plumbing-service" className="hover:text-white">Emergency Plumbing</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Company</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/about" className="hover:text-white">About Us</Link></li>
                  <li><Link href="/blog" className="hover:text-white">Blog</Link></li>
                  <li><Link href="/contact" className="hover:text-white">Contact</Link></li>
                  <li><Link href="/services" className="hover:text-white">All Services</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 {companyName}. All rights reserved. | Licensed & Insured | Emergency Service Available</p>
              <p className="mt-2 text-sm">Licensed Plumbing Contractor | Serving Kane County</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
} 